import { Button, ButtonMode, ButtonSize, Checkbox, Popup } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React from 'react';
import { ContentTypeFilter } from '../../../../../../models';
import { NotificationMode, RiskProductAlert } from '../../../../../../models/Preference';
import WatchlistAlert from '../../../../WatchlistsTab/WatchlistAlert/WatchlistAlert';
import styles from './AlertMultiProductModeRenderer.module.scss';

const cx = classNames.bind(styles);

interface ModePayload {
  mode: NotificationMode;
  modeResearch?: NotificationMode;
  modeCovenants?: NotificationMode;
  modeNews?: NotificationMode;
}

const AlertMultiProductModeRenderer = ({
  item,
  row,
  props,
  children,
}: {
  item: {
    tags: string[];
    mode: NotificationMode;
    modeResearch?: NotificationMode;
    modeCovenants?: NotificationMode;
    modeNews?: NotificationMode;
    riskProductAlert?: RiskProductAlert;
  };
  row?: { index: number };
  props?: {
    onChange: (mode: ModePayload, index: number) => void;
    showResearch: boolean;
    showCovenants: boolean;
    showNews: boolean;
    buttonSize?: ButtonSize;
    isDisabled?: boolean;
  };
  children?: React.ReactElement;
}): React.ReactElement => {
  const { onChange, showResearch, showCovenants, showNews } = props || {};
  const mode = item.mode || NotificationMode.None;
  const modeResearch = showResearch ? item.modeResearch || mode : NotificationMode.None;
  const modeCovenants = showCovenants ? item.modeCovenants || mode : NotificationMode.None;
  const modeNews = showNews ? item.modeNews || mode : NotificationMode.None;
  const isAnalyst = item.tags.some((tag) => /(^analyst)/.test(tag));
  const isReport = item.tags.some((tag) => /(^type-tag)/.test(tag));
  const anyAlertMode = [modeResearch, modeCovenants, modeNews].find((item) => item !== NotificationMode.None);

  const onSubmit = (payload: Partial<ModePayload>) => {
    onChange?.(
      {
        mode: NotificationMode.None,
        modeResearch: payload.mode ? payload.mode : modeResearch,
        modeCovenants: payload.mode ? payload.mode : modeCovenants,
        modeNews: payload.mode ? payload.mode : modeNews,
        ...(payload.mode ? {} : payload),
      },
      row?.index ?? -1
    );
  };

  const renderBlock = (type: ContentTypeFilter | 'analyst' | 'report') => {
    let title: string;
    let fieldName;
    let value;

    switch (type) {
      case ContentTypeFilter.RESEARCH:
        if (!showResearch || isAnalyst || isReport) {
          return null;
        }

        title = 'Research Alerts';
        fieldName = 'modeResearch';
        value = modeResearch;
        break;
      case ContentTypeFilter.COVENANTS:
        if (!showCovenants || isAnalyst || isReport) {
          return null;
        }

        title = 'Covenants Alerts';
        fieldName = 'modeCovenants';
        value = modeCovenants;
        break;
      case ContentTypeFilter.NEWS:
        if (!showNews || isAnalyst || isReport) {
          return null;
        }

        title = 'News Alerts';
        fieldName = 'modeNews';
        value = modeNews;
        break;
      case 'analyst':
        if (!isAnalyst) {
          return null;
        }

        title = 'Analyst Alerts';
        fieldName = 'mode';
        value = anyAlertMode || mode;
        break;
      case 'report':
        if (!isReport) {
          return null;
        }

        title = 'Report Alerts';
        fieldName = 'mode';
        value = anyAlertMode || mode;
        break;
      default:
        return null;
    }

    return (
      <div className={cx('block')}>
        <div className={cx('title')}>
          <div>{title}</div>
          <div>
            <Checkbox
              toggle
              dataTestId=""
              name={fieldName}
              value={NotificationMode.None}
              checked={value !== NotificationMode.None}
              onChange={(_, { checked }) =>
                onSubmit({ [fieldName]: checked ? NotificationMode.Instant : NotificationMode.None })
              }
            />
          </div>
        </div>
        {value !== NotificationMode.None ? (
          <div className={cx('content')}>
            <div className={cx('content-caption')}>Email frequency</div>
            <div>
              <Checkbox
                dataTestId=""
                name={fieldName}
                value={NotificationMode.Instant}
                checked={value === NotificationMode.Instant}
                onChange={() => onSubmit({ [fieldName]: NotificationMode.Instant })}
                label="Instant"
                radio
              />
            </div>
            <div>
              <Checkbox
                dataTestId=""
                name={fieldName}
                value={NotificationMode.Digest}
                checked={value === NotificationMode.Digest}
                onChange={() => onSubmit({ [fieldName]: NotificationMode.Digest })}
                label="Daily Digest"
                radio
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <Popup
      trigger={
        <Button mode={ButtonMode.TERTIARY} size={props?.buttonSize} disabled={props?.isDisabled} hasEqualSidesPadding>
          <WatchlistAlert
            isAnyAlertSelected={
              !!anyAlertMode || (item.riskProductAlert ?? RiskProductAlert.None) !== RiskProductAlert.None
            }
          />
        </Button>
      }
      on="click"
      position="bottom left"
    >
      {renderBlock(ContentTypeFilter.RESEARCH)}
      {renderBlock(ContentTypeFilter.COVENANTS)}
      {renderBlock(ContentTypeFilter.NEWS)}
      {renderBlock('analyst')}
      {renderBlock('report')}
      {children}
    </Popup>
  );
};

export default AlertMultiProductModeRenderer;
